import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { api } from "@/lib/api";
import { Star, Heart, ArrowLeft } from "@phosphor-icons/react";
import { ReviewSubmitModal } from "@/components/ReviewSubmitModal";

export default function NewReview() {
  const [params] = useSearchParams();
  const projectIdFromQuery = params.get("project_id");
  const [eligible, setEligible] = useState(null);
  const [modal, setModal] = useState({ open: false, project: null });
  const [submitted, setSubmitted] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/reviews/me/eligible");
      setEligible(data);
    } catch (_) {
      setEligible({ eligible_projects: [], can_leave_general: true });
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-open the modal if a project_id was passed in the URL
  useEffect(() => {
    if (!eligible || submitted) return;
    if (projectIdFromQuery) {
      const proj = eligible.eligible_projects.find((p) => p.id === projectIdFromQuery);
      if (proj) setModal({ open: true, project: proj });
    }
  }, [eligible, projectIdFromQuery, submitted]);

  if (!eligible) {
    return <ClientLayout><div className="text-[#a3a3a3]">Loading…</div></ClientLayout>;
  }

  if (submitted) {
    return (
      <ClientLayout>
        <div className="max-w-xl mx-auto text-center py-16" data-testid="review-thanks">
          <Heart size={36} weight="fill" className="text-[#FF2D2D] mx-auto" />
          <h1 className="font-display text-5xl mt-6">Thank you.</h1>
          <p className="text-[#a3a3a3] mt-3">Your review is in. We review submissions before publishing — once approved, it'll appear on our homepage.</p>
          <Link to="/dashboard" className="btn-outline mt-8 inline-flex px-5 py-3 text-sm uppercase tracking-wider">
            Back to dashboard
          </Link>
        </div>
      </ClientLayout>
    );
  }

  const hasNothing = eligible.eligible_projects.length === 0 && !eligible.can_leave_general;

  return (
    <ClientLayout>
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#a3a3a3] hover:text-white mb-6">
        <ArrowLeft size={14} /> Dashboard
      </Link>

      <div className="max-w-2xl">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Reviews</div>
        <h1 className="font-display text-5xl mt-3">Tell us how it went.</h1>
        <p className="text-[#a3a3a3] mt-3 text-sm leading-relaxed">
          Your honest feedback helps us improve and gives prospective clients confidence. Reviews are moderated before going public — we'll never publish your email address.
        </p>

        {hasNothing ? (
          <div className="mt-10 border border-[#262626] bg-[#0a0a0a] p-6 text-sm text-[#a3a3a3]" data-testid="no-eligible">
            You've already submitted reviews for everything you're eligible to review. Thanks for the love!
          </div>
        ) : (
          <div className="mt-10 space-y-3" data-testid="eligible-list">
            {eligible.eligible_projects.map((p) => (
              <button
                key={p.id}
                onClick={() => setModal({ open: true, project: p })}
                data-testid={`review-project-${p.id}`}
                className="w-full text-left border border-[#262626] bg-[#0a0a0a] hover:border-[#FF2D2D] hover:bg-[#FF2D2D]/5 p-4 flex items-center justify-between gap-3 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">Project</div>
                  <div className="font-semibold truncate mt-0.5">{p.title}</div>
                  {p.service_type && <div className="text-xs text-[#a3a3a3] capitalize mt-0.5">{p.service_type.replace(/_/g, " ")}</div>}
                </div>
                <div className="flex items-center gap-1 text-[#FF2D2D]">
                  <Star size={12} weight="fill" />
                  <span className="text-xs uppercase tracking-wider">Review →</span>
                </div>
              </button>
            ))}
            {eligible.can_leave_general && (
              <button
                onClick={() => setModal({ open: true, project: null })}
                data-testid="review-general"
                className="w-full text-left border border-dashed border-[#262626] bg-[#0a0a0a] hover:border-[#FF2D2D] hover:bg-[#FF2D2D]/5 p-4 flex items-center justify-between gap-3 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">General</div>
                  <div className="font-semibold mt-0.5">Leave a general testimonial</div>
                  <div className="text-xs text-[#a3a3a3] mt-0.5">Not tied to a specific project — for ongoing work, maintenance plans, etc.</div>
                </div>
                <div className="flex items-center gap-1 text-[#FF2D2D]">
                  <Star size={12} weight="fill" />
                  <span className="text-xs uppercase tracking-wider">Review →</span>
                </div>
              </button>
            )}
          </div>
        )}
      </div>

      <ReviewSubmitModal
        open={modal.open}
        project={modal.project}
        onClose={() => setModal({ open: false, project: null })}
        onSubmitted={() => { setSubmitted(true); load(); }}
      />
    </ClientLayout>
  );
}
